import { Component } from '@theme/component';

const REVIEWS_WIDGET_SCRIPT_URL = 'https://widget.reviews.io/polaris/build.js';
const REVIEW_SUMMARY_API_URL = 'https://api.reviews.io/reviews';

let reviewsWidgetScriptPromise;
const reviewSummaryCache = new Map();

function getReviewSummaryCacheKey(storeId, sku) {
  return `${storeId}::${sku}`;
}

function fetchReviewSummary({ storeId, sku }) {
  const cacheKey = getReviewSummaryCacheKey(storeId, sku);

  if (reviewSummaryCache.has(cacheKey)) {
    return reviewSummaryCache.get(cacheKey);
  }

  const request = fetch(
    `${REVIEW_SUMMARY_API_URL}?store=${encodeURIComponent(storeId)}&sku=${encodeURIComponent(
      sku
    )}&type=product_review&include_subrating_breakdown=false&per_page=1`
  )
    .then((response) => {
      if (!response.ok) {
        throw new Error(`Reviews.io summary request failed with status ${response.status}`);
      }

      return response.json();
    })
    .catch((error) => {
      reviewSummaryCache.delete(cacheKey);
      throw error;
    });

  reviewSummaryCache.set(cacheKey, request);

  return request;
}

async function hasAnyReviews({ storeId, skuList }) {
  const skus = skuList
    .split(';')
    .map((sku) => sku.trim())
    .filter(Boolean);

  if (!skus.length) return false;

  const results = await Promise.allSettled(skus.map((sku) => fetchReviewSummary({ storeId, sku })));
  let hadFailedRequest = false;

  for (const result of results) {
    if (result.status === 'rejected') {
      hadFailedRequest = true;
      continue;
    }

    const reviewCount = Number.parseInt(result.value?.review_count, 10) || 0;
    if (reviewCount > 0) {
      return true;
    }
  }

  // Fail open if the API request was inconclusive, rather than hiding reviews by mistake.
  return hadFailedRequest;
}

function loadReviewsWidgetScript() {
  if (window.ReviewsWidget) {
    return Promise.resolve(window.ReviewsWidget);
  }

  if (reviewsWidgetScriptPromise) {
    return reviewsWidgetScriptPromise;
  }

  reviewsWidgetScriptPromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector(`script[src="${REVIEWS_WIDGET_SCRIPT_URL}"]`);

    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(window.ReviewsWidget), { once: true });
      existingScript.addEventListener('error', reject, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = REVIEWS_WIDGET_SCRIPT_URL;
    script.async = true;
    script.addEventListener('load', () => resolve(window.ReviewsWidget), { once: true });
    script.addEventListener('error', reject, { once: true });
    document.head.append(script);
  });

  return reviewsWidgetScriptPromise;
}

class ProductReviewsWidget extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.initialize();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.headerGroupObserver?.disconnect();
  }

  syncHeaderGroupState() {
    const centerGroup = this.querySelector('.header__group--center');
    if (!centerGroup) return;

    const sliderGroup = centerGroup.querySelector('.R-SliderIndicator-group');
    const hasSkills = Boolean(sliderGroup && sliderGroup.children.length > 0);

    centerGroup.classList.toggle('is-populated', hasSkills);
    centerGroup.toggleAttribute('hidden', !hasSkills);
  }

  observeHeaderGroupState(target) {
    this.headerGroupObserver?.disconnect();

    this.headerGroupObserver = new MutationObserver(() => {
      this.syncHeaderGroupState();
    });

    this.headerGroupObserver.observe(target, {
      childList: true,
      subtree: true,
    });

    this.syncHeaderGroupState();
  }

  hideSection() {
    if (window.Shopify?.designMode) {
      this.hidden = true;
      return;
    }

    const shopifySection = this.closest('.shopify-section');
    if (shopifySection instanceof HTMLElement) {
      shopifySection.hidden = true;
      return;
    }

    const section = this.closest('.section');
    if (section instanceof HTMLElement) {
      section.hidden = true;
      const background = section.previousElementSibling;
      if (background instanceof HTMLElement && background.classList.contains('section-background')) {
        background.hidden = true;
      }
    }
  }

  async initialize() {
    if (this.dataset.initialized === 'true') return;

    const skuList = this.dataset.skuList?.trim();
    const widgetId = this.dataset.widgetId;
    const storeId = this.dataset.storeId || 'fayn';

    if (!skuList || !widgetId) {
      return;
    }

    try {
      const shouldRenderWidget = await hasAnyReviews({ storeId, skuList });
      if (!this.isConnected) return;

      if (!shouldRenderWidget) {
        this.dataset.initialized = 'true';
        this.hideSection();
        return;
      }

      this.dataset.initialized = 'true';
      await loadReviewsWidgetScript();

      if (!this.isConnected || !window.ReviewsWidget) return;

      const target = document.getElementById(widgetId);
      if (!target) return;

      target.replaceChildren();
      this.observeHeaderGroupState(target);

      const perPage = Number.parseInt(this.dataset.perPage || '15', 10);
      const language = this.dataset.language || 'en';
      const grouping = this.dataset.grouping || '';

      new window.ReviewsWidget(`#${widgetId}`, {
        store: storeId,
        widget: 'polaris',
        options: {
          types: 'product_review',
          enable_sentiment_analysis: false,
          lang: language,
          layout: '',
          per_page: Number.isNaN(perPage) ? 15 : perPage,
          store_review: {
            hide_if_no_results: false,
          },
          third_party_review: {
            hide_if_no_results: false,
          },
          product_review: {
            sku: skuList,
            hide_if_no_results: false,
          },
          questions: {
            hide_if_no_results: false,
            enable_ask_question: false,
            enable_ask_question_button_style: false,
            show_dates: true,
            grouping,
          },
          header: {
            enable_summary: true,
            enable_ratings: true,
            enable_attributes: true,
            enable_image_gallery: false,
            enable_percent_recommended: false,
            enable_write_review: false,
            enable_ask_question: false,
            enable_sub_header: true,
            rating_decimal_places: 2,
            use_write_review_button: false,
            enable_if_no_results: false,
            show_review_title_field: false,
          },
          sentiment: {
            badge_text: 'Our Customers Say',
          },
          filtering: {
            enable: true,
            enable_text_search: true,
            enable_sorting: true,
            enable_product_filter: true,
            enable_media_filter: false,
            enable_overall_rating_filter: true,
            enable_language_filter: false,
            enable_language_filter_language_change: false,
            enable_ratings_filters: true,
            enable_attributes_filters: true,
            enable_expanded_filters: false,
          },
          reviews: {
            enable_avatar: true,
            enable_reviewer_name: true,
            enable_reviewer_address: true,
            reviewer_address_format: 'city, country',
            enable_verified_badge: true,
            enable_subscriber_badge: true,
            enable_reviewer_recommends: true,
            enable_attributes: true,
            enable_product_name: true,
            enable_link_product: false,
            enable_review_title: undefined,
            enable_replies: true,
            enable_images: true,
            enable_ratings: true,
            enable_share: true,
            enable_helpful_vote: true,
            enable_helpful_display: true,
            enable_report: true,
            enable_date: true,
            enable_third_party_source: true,
            enable_duplicate_reviews: undefined,
            combine_title_stars: true,
          },
        },
        translations: {
          'Verified Customer': 'Verifizierter Kunde',
        },
        styles: {
          '--base-font-size': '12px',
          '--common-button-font-family': 'var(--font-accent--family)',
          '--common-button-font-size': '14px',
          '--common-button-font-weight': '500',
          '--common-button-letter-spacing': '0',
          '--common-button-text-transform': 'none',
          '--common-button-vertical-padding': '1.125rem',
          '--common-button-horizontal-padding': '2rem',
          '--common-button-border-width': '1px',
          '--common-button-border-radius': '128px',
          '--primary-button-bg-color': '#000000',
          '--primary-button-border-color': '#000000',
          '--primary-button-text-color': '#ffffff',
          '--secondary-button-bg-color': 'transparent',
          '--secondary-button-border-color': '#000000',
          '--secondary-button-text-color': '#000000',
          '--common-star-color': '#000000',
          '--common-star-disabled-color': 'rgba(0,0,0,0.25)',
          '--medium-star-size': '16px',
          '--small-star-size': '14px',
          '--heading-text-color': '#000000',
          '--heading-text-font-weight': '600',
          '--heading-text-font-family': 'var(--font-body--family)',
          '--heading-text-line-height': '1.3',
          '--heading-text-letter-spacing': '0',
          '--heading-text-transform': 'none',
          '--body-text-color': '#000000',
          '--body-text-font-weight': '400',
          '--body-text-font-family': 'var(--font-body--family)',
          '--body-text-line-height': '1.5',
          '--body-text-letter-spacing': '0',
          '--body-text-transform': 'none',
          '--inputfield-text-font-family': 'var(--font-body--family)',
          '--input-text-font-size': '12px',
          '--inputfield-text-font-weight': '400',
          '--inputfield-text-color': '#000000',
          '--inputfield-border-color': '#dfdfdf',
          '--inputfield-background-color': 'transparent',
          '--inputfield-border-width': '0.5px',
          '--inputfield-border-radius': '0px',
          '--common-border-color': '#dfdfdf',
          '--common-border-width': '1px',
          '--common-sidebar-width': '300px',
          '--filters-panel-bg-color': '#f7f7f7',
          '--filters-panel-font-size': '12px',
          '--filters-panel-text-color': '#000000',
          '--filters-panel-horizontal-padding': '32px',
          '--filters-panel-vertical-padding': '32px',
          '--slider-indicator-bg-color': '#ffffff',
          '--slider-indicator-button-color': '#000000',
          '--slider-indicator-width': '100%',
          '--badge-icon-color': '#000000',
          '--badge-icon-font-size': 'inherit',
          '--badge-text-color': '#000000',
          '--badge-text-font-size': 'inherit',
          '--badge-text-letter-spacing': 'inherit',
          '--badge-text-transform': 'inherit',
          '--author-font-size': 'inherit',
          '--author-text-transform': 'none',
          '--avatar-thumbnail-size': '36px',
          '--avatar-thumbnail-border-radius': '100px',
          '--avatar-thumbnail-text-color': '#000000',
          '--avatar-thumbnail-bg-color': 'rgba(0,0,0,0.1)',
          '--photo-video-thumbnail-size': '80px',
          '--photo-video-thumbnail-border-radius': '0px',
          '--mediaslider-scroll-button-icon-color': '#000000',
          '--mediaslider-scroll-button-bg-color': 'rgba(255, 255, 255, 0.85)',
          '--mediaslider-overlay-text-color': '#ffffff',
          '--mediaslider-overlay-bg-color': 'rgba(0, 0, 0, 0.8)',
          '--mediaslider-item-size': '110px',
          '--pagination-tab-text-color': '#000000',
          '--pagination-tab-text-transform': 'none',
          '--pagination-tab-text-letter-spacing': '0',
          '--pagination-tab-text-font-size': '12px',
          '--pagination-tab-text-font-weight': '400',
          '--pagination-tab-active-text-color': '#000000',
          '--pagination-tab-active-text-font-weight': '600',
          '--pagination-tab-active-border-color': '#000000',
          '--pagination-tab-border-width': '1px',
          '--sentiment-base-font-size': '12px',
          '--sentiment-panel-bg-color': '#f7f7f7',
          '--sentiment-panel-border-size': '1px',
          '--sentiment-panel-border-color': 'transparent',
          '--sentiment-panel-border-radius': '0px',
          '--sentiment-panel-shadow-size': '0px',
          '--sentiment-panel-shadow-color': 'rgba(0, 0, 0, 0.1)',
          '--sentiment-heading-text-color': '#000000',
          '--sentiment-heading-text-font-weight': '600',
          '--sentiment-heading-text-font-family': 'var(--font-body--family)',
          '--sentiment-heading-text-line-height': '1.3',
          '--sentiment-heading-text-letter-spacing': '0',
          '--sentiment-heading-text-transform': 'none',
          '--sentiment-body-text-color': '#000000',
          '--sentiment-body-text-font-weight': '400',
          '--sentiment-body-text-font-family': 'var(--font-body--family)',
          '--sentiment-body-text-line-height': '1.5',
          '--sentiment-body-text-letter-spacing': '0',
          '--sentiment-body-text-transform': 'none',
          '--sentiment-panel-vertical-padding': '32px',
          '--sentiment-panel-horizontal-padding': '32px',
          '--sentiment-header-text-color': 'inherit',
          '--sentiment-header-text-font-size': '12px',
          '--sentiment-header-text-font-weight': 'inherit',
          '--sentiment-header-bg-color': 'rgba(0, 0, 0, 0.04)',
          '--sentiment-header-border-radius': '50px',
          '--sentiment-header-shadow-size': '0px',
          '--sentiment-header-shadow-color': 'rgba(0, 0, 0, 0.1)',
          '--sentiment-header-vertical-padding': '7px',
          '--sentiment-header-horizontal-padding': '10px',
          '--sentiment-pagination-tab-text-font-size': '13px',
          '--sentiment-pagination-tab-text-font-weight': '600',
          '--sentiment-pagination-tab-text-color': '#000000',
          '--sentiment-pagination-tab-text-transform': 'none',
          '--sentiment-pagination-tab-text-letter-spacing': '0',
          '--sentiment-pagination-tab-active-text-color': '#000000',
          '--sentiment-pagination-tab-active-text-font-weight': '600',
          '--sentiment-pagination-tab-active-border-color': '#000000',
          '--sentiment-pagination-tab-border-width': '1px',
        },
      });
    } catch (error) {
      console.error('Failed to initialize Reviews.io widget', error);
      this.dataset.initialized = 'false';
    }
  }
}

if (!customElements.get('product-reviews-widget')) {
  customElements.define('product-reviews-widget', ProductReviewsWidget);
}
